import { Capacitor } from '@capacitor/core'
import { Purchases } from '@revenuecat/purchases-capacitor'
import { createSnapshotLoader } from '@/utils/offeringsSnapshot'
import { createRevenueCatOfferingLoader } from '@/services/revenuecatOfferings'

/**
 * @param {unknown} error
 * @returns {boolean}
 */
export function isRevenueCatPurchaseUserCancelled (error) {
  if (!error || typeof error !== 'object') return false
  if (error.userCancelled === true) return true
  const cancelledCode = Purchases?.PURCHASES_ERROR_CODE?.PURCHASE_CANCELLED_ERROR
  const code = /** @type {{ code?: unknown }} */ (error).code
  if (cancelledCode !== undefined && String(code) === String(cancelledCode)) return true
  // Native bridge uses numeric/string "1" for PURCHASE_CANCELLED_ERROR
  if (String(code) === '1') return true
  return false
}

/**
 * Localized message for RevenueCat purchase failures (empty string if user cancelled).
 * @param {unknown} error
 * @param {(key: string) => string} t
 * @returns {string}
 */
export function getRevenueCatPurchaseErrorMessage (error, t) {
  if (!error || isRevenueCatPurchaseUserCancelled(error)) return ''
  const err = /** @type {{ code?: unknown }} */ (error)
  if (err.code === 'RC_MONTHLY_NOT_FOUND') {
    return t('planner.upgrade_page_purchase_no_monthly_package')
  }
  if (err.code === 'RC_ANNUAL_NOT_FOUND' || err.code === 'RC_PACKAGES_INCOMPLETE') {
    return t('planner.upgrade_page_purchase_package_unavailable')
  }
  if (err.code === 'RC_OFFERINGS_FAILED') {
    return t('planner.upgrade_page_native_pricing_error')
  }
  if (err.code === 'RC_PACKAGE_MISSING') {
    return t('planner.upgrade_page_purchase_error')
  }
  if (err.code === 'RC_NOT_CONFIGURED') {
    return t('planner.upgrade_page_purchase_error')
  }
  const code = String(err.code ?? '')
  if (code === '6') {
    return t('planner.upgrade_page_purchase_already_owned')
  }
  if (code === '10' || code === '2') {
    return t('planner.upgrade_page_purchase_network')
  }
  return t('planner.upgrade_page_purchase_error')
}

let isConfigured = false
let configuredForPlatform = null
let currentAppUserId = null

function isNativeMobilePlatform () {
  return Capacitor.isNativePlatform() && ['ios', 'android'].includes(Capacitor.getPlatform())
}

function getApiKeyForCurrentPlatform () {
  const platform = Capacitor.getPlatform()
  if (platform === 'ios') {
    return import.meta.env.VITE_REVENUECAT_API_KEY_IOS || import.meta.env.REVENUECAT_API_KEY_IOS || ''
  }
  if (platform === 'android') {
    return import.meta.env.VITE_REVENUECAT_API_KEY_ANDROID || import.meta.env.REVENUECAT_API_KEY_ANDROID || ''
  }
  return ''
}

export async function initializeRevenueCat () {
  if (!isNativeMobilePlatform()) return false

  const platform = Capacitor.getPlatform()
  const apiKey = getApiKeyForCurrentPlatform()
  if (!apiKey) {
    console.warn('RevenueCat API key missing for platform:', platform)
    return false
  }

  if (isConfigured && configuredForPlatform === platform) {
    return true
  }

  await Purchases.configure({ apiKey })
  isConfigured = true
  configuredForPlatform = platform
  return true
}

export async function loginRevenueCatUser (userId) {
  if (!isNativeMobilePlatform()) return
  if (!userId) return

  const configured = await initializeRevenueCat()
  if (!configured) return
  const normalizedId = String(userId)
  if (currentAppUserId === normalizedId) return

  await Purchases.logIn({ appUserID: normalizedId })
  currentAppUserId = normalizedId
}

export function isRevenueCatSupportedPlatform () {
  return isNativeMobilePlatform()
}

export async function getMonthlyPackageFromCurrentOffering () {
  const { monthly } = await getCurrentOfferingPackages()
  return monthly
}

const loadCurrentOffering = createRevenueCatOfferingLoader({
  loadOfferings: () => Purchases.getOfferings(),
  checkIntroEligibility: options => Purchases.checkTrialOrIntroductoryPriceEligibility(options),
  getPlatform: () => Capacitor.getPlatform(),
})

async function fetchCurrentOfferingPackages () {
  const configured = await initializeRevenueCat()
  if (!configured) {
    const error = new Error('RevenueCat is not configured for this platform')
    error.code = 'RC_NOT_CONFIGURED'
    throw error
  }

  return loadCurrentOffering()
}

const loadOfferingsSnapshot = createSnapshotLoader(fetchCurrentOfferingPackages)

/**
 * Concurrent and near-simultaneous callers share one store request, so the
 * packages that are shown, the trial terms and the package that is purchased
 * all come from the same snapshot. Pass `fresh` to force a new request (retry).
 * @param {{ fresh?: boolean }} [options]
 * @returns {Promise<{ offeringIdentifier: string, monthly: import('@revenuecat/purchases-capacitor').PurchasesPackage, annual: import('@revenuecat/purchases-capacitor').PurchasesPackage, introEligibility: { monthly: boolean, annual: boolean } }>}
 */
export function getCurrentOfferingPackages (options) {
  return loadOfferingsSnapshot(options)
}

/**
 * @param {import('@revenuecat/purchases-capacitor').PurchasesPackage} aPackage
 */
export async function purchaseRevenueCatPackage (aPackage) {
  const configured = await initializeRevenueCat()
  if (!configured) {
    const error = new Error('RevenueCat is not configured for this platform')
    error.code = 'RC_NOT_CONFIGURED'
    throw error
  }
  if (!aPackage || typeof aPackage !== 'object') {
    const error = new Error('No package selected')
    error.code = 'RC_PACKAGE_MISSING'
    throw error
  }

  const plainPackage = JSON.parse(JSON.stringify(aPackage))
  return Purchases.purchasePackage({ aPackage: plainPackage })
}

export async function purchaseRevenueCatMonthlyPackage () {
  const { monthly } = await getCurrentOfferingPackages()
  if (!monthly) {
    const error = new Error('Monthly package not found in current offering')
    error.code = 'RC_MONTHLY_NOT_FOUND'
    throw error
  }

  return purchaseRevenueCatPackage(monthly)
}

export async function restoreRevenueCatPurchases () {
  const configured = await initializeRevenueCat()
  if (!configured) {
    const error = new Error('RevenueCat is not configured for this platform')
    error.code = 'RC_NOT_CONFIGURED'
    throw error
  }

  return Purchases.restorePurchases()
}

export { eligibleFreeTrialDaysFromPackage, freeTrialDaysFromPackage, purchaseStartedFreeTrial } from '@/utils/revenuecatTrial'
